import type { KeyboardCoordinateGetter } from "@dnd-kit/core";

export type ReorderAxis = "horizontal" | "vertical";

type ReorderStep = "previous" | "next" | "first" | "last";

const STEP_KEYS: Record<ReorderAxis, Record<string, ReorderStep>> = {
  horizontal: {
    ArrowLeft: "previous",
    ArrowRight: "next",
    Home: "first",
    End: "last",
  },
  vertical: {
    ArrowUp: "previous",
    ArrowDown: "next",
    Home: "first",
    End: "last",
  },
};

export function reorderCoordinateGetter(
  axis: ReorderAxis,
): KeyboardCoordinateGetter {
  return (event, { context, currentCoordinates }) => {
    const step = STEP_KEYS[axis][event.code];
    if (!step) return undefined;
    const { active, collisionRect, droppableRects, droppableContainers, over } =
      context;
    if (!active || !collisionRect) return undefined;

    const ordered = droppableContainers
      .getEnabled()
      .flatMap((container) => {
        const rect = droppableRects.get(container.id);
        return rect ? [{ id: container.id, rect }] : [];
      })
      .sort((first, second) =>
        axis === "horizontal"
          ? first.rect.left - second.rect.left
          : first.rect.top - second.rect.top,
      );

    const currentId = over?.id ?? active.id;
    const index = ordered.findIndex((entry) => entry.id === currentId);
    if (index < 0) return undefined;

    const targetIndex = {
      previous: index - 1,
      next: index + 1,
      first: 0,
      last: ordered.length - 1,
    }[step];
    if (targetIndex === index) return undefined;
    const target = ordered[targetIndex];
    if (!target) return undefined;

    return axis === "horizontal"
      ? {
          x: target.rect.left + target.rect.width / 2 - collisionRect.width / 2,
          y: currentCoordinates.y,
        }
      : {
          x: currentCoordinates.x,
          y:
            target.rect.top + target.rect.height / 2 - collisionRect.height / 2,
        };
  };
}

export function moveWithin(
  ids: string[],
  id: string,
  delta: number,
): { ordered: string[]; overId: string } | null {
  const from = ids.indexOf(id);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= ids.length) return null;
  const ordered = [...ids];
  ordered.splice(from, 1);
  ordered.splice(to, 0, id);
  return { ordered, overId: ids[to] };
}
